import { css, useTheme } from '@emotion/react';
import { ListChecks, Mic, Pencil, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { fontSizes, spacing } from '@/theme/tokens';

import type { WorkflowStep } from './workflow-utils';

interface NextStepCardProps {
  step: WorkflowStep;
  projectId: string;
  analyzing: boolean;
  disabled: boolean;
  generationActive: boolean;
  hasRehearsal: boolean;
  sectionCount: number;
  onAnalyze: () => void;
  onGenerate: () => void;
  onAcceptSections: () => void;
  onSkipRehearsal?: () => void;
}

export function NextStepCard({
  step,
  projectId,
  analyzing,
  disabled,
  hasRehearsal,
  sectionCount,
  onAnalyze,
  onAcceptSections,
  onSkipRehearsal,
}: NextStepCardProps) {
  const theme = useTheme();

  const cardStyles = css`
    border: 1px solid ${theme.colors.border};
    border-radius: 12px;
    background: ${theme.colors.surface};
    padding: ${spacing.lg};
    text-align: center;
    margin-bottom: ${spacing.md};
  `;

  const titleStyles = css`
    margin: 0 0 ${spacing.sm};
    font-size: ${fontSizes.md};
    color: ${theme.colors.text};
    font-weight: 600;
  `;

  const descStyles = css`
    margin: 0 0 ${spacing.md};
    font-size: ${fontSizes.sm};
    color: ${theme.colors.textSecondary};
    line-height: 1.5;
  `;

  const primaryButtonOverrides = css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing.sm};
  `;

  if (step === 'write') {
    return (
      <div css={cardStyles} data-testid="next-step-card">
        <Pencil
          size={40}
          aria-hidden="true"
          css={css`
            color: ${theme.colors.primary};
            margin-bottom: ${spacing.md};
          `}
        />
        <h3 css={titleStyles}>Write Your Script</h3>
        <p css={descStyles}>
          Write or paste your speech in the editor. Use <code>---</code> on its own line to separate
          sections.
        </p>
      </div>
    );
  }

  if (step === 'analyze') {
    return (
      <div css={cardStyles} data-testid="next-step-card">
        <Sparkles
          size={40}
          aria-hidden="true"
          css={css`
            color: ${theme.colors.primary};
            margin-bottom: ${spacing.md};
          `}
        />
        <h3 css={titleStyles}>Detect Sections</h3>
        <p css={descStyles}>AI will identify sections and analyze the tone of each one.</p>
        <Button
          size="sm"
          onClick={onAnalyze}
          disabled={analyzing || disabled}
          css={primaryButtonOverrides}
          data-testid="analyze-button"
        >
          {analyzing ? 'Detecting...' : 'Detect Sections'}
        </Button>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div css={cardStyles} data-testid="next-step-card">
        <ListChecks
          size={40}
          aria-hidden="true"
          css={css`
            color: ${theme.colors.primary};
            margin-bottom: ${spacing.md};
          `}
        />
        <h3 css={titleStyles}>Review Sections</h3>
        <p css={descStyles}>
          {sectionCount} {sectionCount === 1 ? 'section' : 'sections'} detected. Review the
          analysis, or continue.
        </p>
        <Button
          size="sm"
          onClick={onAcceptSections}
          css={primaryButtonOverrides}
          data-testid="accept-sections-button"
        >
          Accept &amp; Continue
        </Button>
        <p
          css={css`
            margin: ${spacing.sm} 0 0;
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textMuted};
            text-align: center;
          `}
        >
          or
        </p>
        <button
          type="button"
          onClick={onAnalyze}
          disabled={analyzing || disabled}
          css={css`
            margin: ${spacing.xs} 0 0;
            padding: 0;
            display: inline-flex;
            align-items: center;
            gap: 3px;
            border: none;
            background: none;
            font-size: ${fontSizes.xs};
            color: ${theme.colors.textSecondary};
            cursor: pointer;

            &:hover:not(:disabled) {
              color: ${theme.colors.text};
              text-decoration: underline;
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}
          data-testid="reanalyze-button"
        >
          <Sparkles size={12} aria-hidden="true" />
          {analyzing ? 'Analyzing...' : 'Reanalyze Sections'}
        </button>
      </div>
    );
  }

  if (step === 'rehearse') {
    return (
      <div css={cardStyles} data-testid="next-step-card">
        <Mic
          size={40}
          aria-hidden="true"
          css={css`
            color: ${theme.colors.primary};
            margin-bottom: ${spacing.md};
          `}
        />
        <h3 css={titleStyles}>Rehearse Your Script</h3>
        <p css={descStyles}>Practice your delivery to capture natural timing for each section.</p>
        <Link
          to={`/app/projects/${projectId}/rehearse`}
          css={css`
            ${primaryButtonOverrides};
            text-decoration: none;
            padding: ${spacing.sm} ${spacing.md};
            font-size: ${fontSizes.sm};
            border-radius: 980px;
            background: ${theme.colors.primary};
            color: ${theme.colors.primaryText};
            font-weight: 600;
            transition: background 0.2s ease-in-out;

            &:hover {
              background: ${theme.colors.primaryHover};
            }
          `}
          data-testid="rehearse-button"
        >
          <Mic size={16} aria-hidden="true" />
          Start Rehearsal
        </Link>
        {!hasRehearsal && onSkipRehearsal && (
          <>
            <p
              css={css`
                margin: ${spacing.sm} 0 0;
                font-size: ${fontSizes.xs};
                color: ${theme.colors.textMuted};
                text-align: center;
              `}
            >
              or
            </p>
            <button
              type="button"
              onClick={onSkipRehearsal}
              css={css`
                margin: ${spacing.xs} 0 0;
                padding: 0;
                border: none;
                background: none;
                font-size: ${fontSizes.xs};
                color: ${theme.colors.textSecondary};
                cursor: pointer;

                &:hover {
                  color: ${theme.colors.text};
                  text-decoration: underline;
                }
              `}
              data-testid="skip-rehearsal-link"
            >
              Set speaking pace
            </button>
          </>
        )}
      </div>
    );
  }

  // step === 'generate' or 'complete' are handled by parent
  return null;
}
